import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthorization } from 'hooks';
import { Grid, Checkbox, FormControlLabel } from '@mui/material';
import { Button, TextField } from '@totalsoft/rocket-ui';
import { MuiTelInput } from 'mui-tel-input';
import PropTypes from 'prop-types';
import { onCheckBoxChange, onTelInputChange } from 'utils/propertyChangeAdapters';
import { valueOrDefault } from 'utils/functions';
import { emptyString } from 'constants/defaultValues';
import { isValid, getErrors } from '@totalsoft/pure-validations-react';
import { get, set } from '@totalsoft/change-tracking-react';
import InsuranceDataContext from 'features/insuranceDocument/stepper/insuranceDataContext';
import AuthorizationCodes from 'features/common/constants/authorizationCodes';
import { DriverSummaryWrapper } from './DriverStyles';
import { DetatchedAccordion } from 'components/common/SurfaceStyles';

const DriverComponent = ({
  driver,
  defaultCountryCode,
  onDeleteDriver,
  index,
  validation,
  onUniqueIDChange,
  onPanelChange,
  expanded,
  readOnly
}) => {
  const { documentTypeId, documentStateId } = useContext(InsuranceDataContext);
  const hasRightToEditDrivers = useAuthorization(AuthorizationCodes.hasRightToEditDrivers, documentTypeId, documentStateId);
  const { t } = useTranslation();

  return (
    <Grid item xs={12} key={index}>
      <DetatchedAccordion
        expanded={expanded}
        onChange={(_, isExpand) => onPanelChange(isExpand)}
        title={
          <DriverSummaryWrapper>
            {!expanded && (
              <>
                <div>
                  <strong>{t('Offer.Edit.DriverData.Name')}:</strong>
                  {`${driver.firstName |> get} ${driver.lastName |> get}`}
                </div>
                <div>
                  <strong>{t('Offer.Edit.DriverData.UniqueID')}:</strong>
                  {driver.uniqueID |> get}
                </div>
              </>
            )}
          </DriverSummaryWrapper>
        }
        content={
          <Grid container spacing={2} justifyContent='space-between'>
            <Grid item xs={12} sm={6} lg={4}>
              <FormControlLabel
                control={
                  <Checkbox
                    id={'driver_GDPR_' + index}
                    checked={driver.agreesWithPersonalDataUse |> get |> valueOrDefault(false)}
                    disabled={readOnly({
                      controlName: 'Driver.GDPR',
                      hasAuthorization: hasRightToEditDrivers
                    })}
                    onChange={driver.agreesWithPersonalDataUse |> set |> onCheckBoxChange}
                  />
                }
                label={t('Offer.Edit.DriverData.GDPR')}
              ></FormControlLabel>
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <TextField
                id={'driver_uniqueID_' + index}
                required
                fullWidth
                value={driver.uniqueID |> get}
                label={t('Offer.Edit.DriverData.UniqueID')}
                disabled={
                  !(driver.agreesWithPersonalDataUse |> get) ||
                  readOnly({
                    controlName: 'Driver.UniqueID',
                    hasAuthorization: hasRightToEditDrivers
                  })
                }
                onChange={onUniqueIDChange}
                error={!isValid(validation.uniqueID)}
                helperText={getErrors(validation.uniqueID)}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <TextField
                id={'driver_IdentityCardNo' + index}
                required
                fullWidth
                value={driver.identityCardNo |> get |> valueOrDefault(emptyString)}
                label={t('Offer.Edit.DriverData.IdentityCardNo')}
                disabled={
                  !(driver.agreesWithPersonalDataUse |> get) ||
                  readOnly({
                    controlName: 'Driver.IdentityCardNo',
                    hasAuthorization: hasRightToEditDrivers
                  })
                }
                onChange={driver.identityCardNo |> set}
                error={!isValid(validation.identityCardNo)}
                helperText={getErrors(validation.identityCardNo)}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <TextField
                fullWidth
                required
                id={'driver_FirstName_' + index}
                onChange={driver.firstName |> set}
                label={t('Offer.Edit.DriverData.FirstName')}
                value={driver.firstName |> get |> valueOrDefault(emptyString)}
                disabled={
                  !(driver.agreesWithPersonalDataUse |> get) ||
                  readOnly({
                    controlName: 'Driver.FirstName',
                    hasAuthorization: hasRightToEditDrivers
                  })
                }
                error={!isValid(validation.firstName)}
                helperText={getErrors(validation.firstName)}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <TextField
                fullWidth
                required
                id={'driver_LastName_' + index}
                onChange={driver.lastName |> set}
                label={t('Offer.Edit.DriverData.LastName')}
                value={driver.lastName |> get |> valueOrDefault(emptyString)}
                disabled={
                  !(driver.agreesWithPersonalDataUse |> get) ||
                  readOnly({
                    controlName: 'Driver.LastName',
                    hasAuthorization: hasRightToEditDrivers
                  })
                }
                error={!isValid(validation.lastName)}
                helperText={getErrors(validation.lastName)}
              />
            </Grid>
            <Grid item xs={12} sm={6} lg={4}>
              <MuiTelInput
                id={'driver_PhoneNumber' + index}
                defaultCountry={defaultCountryCode}
                country={driver.phoneCountryCode |> get}
                inputComponent={TextField}
                fullWidth
                required
                size='small'
                variant='standard'
                label={t('Partner.PhoneNumber')}
                value={driver.phoneNumber |> get |> valueOrDefault(emptyString)}
                onChange={onTelInputChange(driver.phoneNumber |> set, driver.phoneCountryCode |> set)}
                disabled={
                  !(driver.agreesWithPersonalDataUse |> get) ||
                  readOnly({
                    controlName: 'Driver.PhoneNumber',
                    hasAuthorization: hasRightToEditDrivers
                  })
                }
                error={!isValid(validation.phoneNumber)}
                helperText={getErrors(validation.phoneNumber)}
              />
            </Grid>
            {expanded && (
              <Grid mt={2} container justifyContent='end'>
                <Button
                  onClick={() => onDeleteDriver(index)}
                  disabled={readOnly({
                    controlName: 'Driver',
                    hasAuthorization: hasRightToEditDrivers
                  })}
                >
                  {t('Tooltips.Delete')}
                </Button>
              </Grid>
            )}
          </Grid>
        }
      />
    </Grid>
  );
};

DriverComponent.propTypes = {
  driver: PropTypes.object.isRequired,
  defaultCountryCode: PropTypes.string,
  index: PropTypes.number.isRequired,
  onDeleteDriver: PropTypes.func.isRequired,
  onUniqueIDChange: PropTypes.func.isRequired,
  onPanelChange: PropTypes.func.isRequired,
  expanded: PropTypes.bool.isRequired,
  defaultDriver: PropTypes.object,
  validation: PropTypes.object,
  readOnly: PropTypes.func.isRequired
};

export default DriverComponent;
